import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { MOCK_SPECIES } from '../data/mockSpecies';
import { MapPin, Fish, Anchor, Scale, Ruler, Info, ArrowLeft } from 'lucide-react';

export const SpeciesDetailPage: React.FC = () => {
    const { slug } = useParams();
    const species = MOCK_SPECIES.find(s => s.slug === slug);

    if (!species) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center text-white">
                <h1 className="text-4xl font-bold mb-4">Peixe não encontrado</h1>
                <Link to="/wikipesca/peixes" className="text-brand-gold underline">Voltar para a lista</Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen pb-24">
             {/* Hero Header */}
             <div className="relative h-[60vh] min-h-[500px]">
                <div className="absolute inset-0">
                    <img src={species.coverImage} alt={species.commonNames[0]} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/50 to-transparent"></div>
                </div>
                
                <div className="absolute bottom-0 left-0 w-full p-8 pb-12 max-w-7xl mx-auto">
                    <Link to="/wikipesca/peixes" className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors font-bold uppercase tracking-widest text-xs mb-6 backdrop-blur-md bg-black/20 px-3 py-1 rounded-full">
                        <ArrowLeft size={16} /> Voltar para Espécies
                    </Link>
                    <h1 className="text-5xl md:text-7xl font-display font-black text-white uppercase mb-2 drop-shadow-2xl">
                        {species.commonNames[0]}
                    </h1>
                    <p className="text-2xl text-white/60 font-serif italic mb-6">{species.scientificName}</p>
                    
                    <div className="flex flex-wrap gap-4">
                        <span className="bg-brand-blue-DEFAULT/20 border border-brand-blue-DEFAULT/30 text-brand-blue-light px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider backdrop-blur-md">
                            Família: {species.family}
                        </span>
                        
                        {species.diet?.map(d => (
                             <span key={d} className="bg-white/10 border border-white/10 text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider backdrop-blur-md">
                                {d === 'carnivore' ? 'Carnívoro' : d}
                            </span>
                        ))}
                    </div>
                </div>
             </div>

             <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-3 gap-12 -mt-8 relative z-10">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-12">
                    {/* Intro */}
                    <div className="bg-[#1E293B] border border-white/5 p-8 rounded-3xl shadow-xl">
                        <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                            <Info className="text-brand-gold" />
                            Sobre a Espécie
                        </h2>
                        <p className="text-white/80 leading-relaxed text-lg whitespace-pre-line">
                            {species.description}
                        </p>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-[#1E293B] border border-white/5 p-6 rounded-2xl flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400">
                                <Scale size={24} />
                            </div>
                            <div>
                                <p className="text-white/40 text-xs font-bold uppercase">Peso Máximo</p>
                                <p className="text-2xl font-black text-white">{species.maxWeight} kg</p>
                            </div>
                        </div>
                        <div className="bg-[#1E293B] border border-white/5 p-6 rounded-2xl flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center text-green-400">
                                <Ruler size={24} />
                            </div>
                            <div>
                                <p className="text-white/40 text-xs font-bold uppercase">Tamanho Máx.</p>
                                <p className="text-2xl font-black text-white">{species.maxLength} cm</p>
                            </div>
                        </div>
                    </div>

                     {/* Habitat & Distribution */}
                    <div className="bg-[#1E293B] border border-white/5 p-8 rounded-3xl shadow-xl">
                        <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                            <MapPin className="text-brand-gold" />
                            Habitat & Distribuição
                        </h2>
                        <p className="text-white/80 mb-6">{species.habitat}</p>
                        
                        <div className="flex flex-wrap gap-2">
                             {species.distribution.map(place => (
                                 <span key={place} className="px-3 py-1 bg-white/5 rounded-full text-sm text-white/70 border border-white/10">
                                     {place}
                                 </span>
                             ))}
                        </div>
                    </div>
                </div>

                {/* Sidebar - Fishing Guide */}
                <div className="space-y-6">
                    <div className="bg-brand-gold text-brand-midnight p-8 rounded-3xl shadow-glow-gold relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-8 opacity-10">
                            <Anchor size={120} />
                        </div>
                        <h3 className="text-2xl font-black uppercase mb-6 relative z-10">Como Pescar</h3>
                        
                        <div className="space-y-6 relative z-10">
                            <div>
                                <h4 className="font-bold text-brand-midnight/60 uppercase text-xs mb-2">Melhores Iscas</h4>
                                <ul className="space-y-2">
                                    {species.bestBaits.map(bait => (
                                        <li key={bait} className="flex items-center gap-2 font-bold text-lg">
                                            <span className="w-1.5 h-1.5 bg-brand-midnight rounded-full"></span>
                                            {bait}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <hr className="border-brand-midnight/10" />

                            <div>
                                <h4 className="font-bold text-brand-midnight/60 uppercase text-xs mb-2">Técnicas</h4>
                                <ul className="space-y-2">
                                    {species.bestTechniques.map(tech => (
                                        <li key={tech} className="flex items-center gap-2 font-medium">
                                            <span className="w-1.5 h-1.5 bg-brand-midnight rounded-full"></span>
                                            {tech}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                     <div className="bg-[#1E293B] border border-white/5 p-6 rounded-3xl">
                        <h3 className="text-lg font-bold text-white mb-4">Capturas da Comunidade</h3>
                        <p className="text-white/40 text-sm mb-4">Fotos recentes de {species.commonNames[0]} enviadas por usuários.</p>
                        
                        <div className="grid grid-cols-3 gap-2">
                            {[1,2,3,4,5,6].map(i => (
                                <div key={i} className="aspect-square bg-black/20 rounded-lg animate-pulse" title="Feed de capturas virá aqui"></div>
                            ))}
                        </div>
                     </div>
                </div>
             </div>
        </div>
    );
};
